import { ClipboardCheckIcon } from 'components/exceptions/icons/VisionIcons'
import Constants from 'Constants'
import {
  canSeeCustomersTab,
  canSeeRouteTab,
  canSeeBillingTab,
  canSeeReportTab,
  canSeeReportingTab,
  canSeePricingTab,
} from 'data/permissions/permissionsSelectors'
import { getIsInventoryFeatureEnabled, getIsMessagingFeatureEnabled } from 'data/tenant/tenantSettingsSelectors'
import allRoutes from 'router/routes'
import T from 'T'

const { CAN_SEE_REPORTS } = Constants
const { ROUTE, BILLING, REPORTS, PRICING, CUSTOMERS, REPORTING, INVENTORY: INVENTORY_TITLE, MESSAGING, REVIEW } = T

const { app } = allRoutes
const {
  accountGroups,
  accounts,
  accountsReceivable,
  billing,
  configuredServiceDetails,
  createInvoices,
  createManualInvoices,
  customerDetails,
  customers,
  inventory,
  inventoryContainers,
  addContainer,
  editContainer,
  invoiceGroups,
  invoices,
  lateFeeReview,
  locations,
  paymentGroups,
  payments,
  pricing,
  pricingFees,
  pricingLateFeeDetails,
  pricingLateFees,
  pricingRentalFeeDetails,
  pricingRentalFees,
  pricingDisposalFeeDetails,
  pricingDisposalFees,
  pricingServiceGroups,
  pricingServices,
  pricingTaxes,
  recentlyAdded,
  recentlyViewed,
  reporting,
  reportingDetails,
  reports,
  routes,
  suspensionGroups,
  workOrderDetails,
  workOrdersReview,
  workOrdersReviewV2,
  messaging,
  messagingDetail,
  visionExceptions,
  visionExceptionsDetails,
} = app

type TenantSettings = Record<string, boolean>

export const getAllMenuItems = (tenantSettings: TenantSettings, isReviewEnabled: boolean) => {
  return [
    {
      title: CUSTOMERS,
      route: customers,
      icon: 'Customers.svg',
      childList: [
        `${accountGroups}/:id`,
        `${suspensionGroups}/:id`,
        accountGroups,
        accounts,
        configuredServiceDetails,
        customerDetails,
        locations,
        recentlyAdded,
        recentlyViewed,
        suspensionGroups,
        workOrderDetails,
      ],
      permission: canSeeCustomersTab(),
      isFeatureAvailable: true,
    },
    {
      title: `${ROUTE}s`,
      route: routes,
      icon: 'Route.svg',
      childList: [routes],
      permission: canSeeRouteTab(),
      isFeatureAvailable: true,
    },
    {
      title: BILLING,
      route: createInvoices,
      icon: 'Billing.svg',
      childList: [
        `${invoiceGroups}/:id`,
        `${paymentGroups}/:id`,
        accountsReceivable,
        billing,
        createInvoices,
        createManualInvoices,
        invoiceGroups,
        invoices,
        lateFeeReview,
        paymentGroups,
        payments,
        workOrdersReview,
        workOrdersReviewV2,
      ],
      permission: canSeeBillingTab(),
      isFeatureAvailable: true,
    },
    {
      title: REPORTS,
      route: reports,
      icon: 'Reports.svg',
      childList: [reports],
      permission: canSeeReportTab(),
      isFeatureAvailable: CAN_SEE_REPORTS,
    },
    {
      title: PRICING,
      route: pricing,
      icon: 'Pricing.svg',
      childList: [
        pricing,
        pricingFees,
        pricingLateFeeDetails,
        pricingLateFees,
        pricingRentalFeeDetails,
        pricingRentalFees,
        pricingDisposalFeeDetails,
        pricingDisposalFees,
        pricingServiceGroups,
        pricingServices,
        pricingTaxes,
      ],
      permission: canSeePricingTab(),
      isFeatureAvailable: true,
    },
    {
      title: REPORTING,
      route: reporting,
      icon: 'Reports.svg',
      childList: [reporting, reportingDetails],
      permission: canSeeReportingTab(),
      isFeatureAvailable: true,
    },
    {
      title: INVENTORY_TITLE,
      route: inventory,
      icon: 'Inventory.svg',
      childList: [inventory, inventoryContainers, addContainer, editContainer],
      permission: true,
      // The selector is overridden to () => true for now; keep the call so it still works once that override goes.
      isFeatureAvailable: (getIsInventoryFeatureEnabled as (ts: TenantSettings) => boolean)(tenantSettings),
    },
    {
      title: MESSAGING,
      route: messaging,
      icon: 'Email/Mail.svg',
      iconFilter: 'invert(1) sepia(100%) saturate(0%) hue-rotate(184deg) brightness(215%) contrast(101%)',
      childList: [messaging, messagingDetail],
      permission: true,
      isFeatureAvailable: getIsMessagingFeatureEnabled(tenantSettings),
    },
    {
      title: REVIEW,
      route: visionExceptions,
      IconComponent: ClipboardCheckIcon,
      childList: [visionExceptions, visionExceptionsDetails],
      permission: true,
      isFeatureAvailable: isReviewEnabled,
    },
  ]
}
